<template>
  <div class="pre-checkin-offers-management">
    <b-card>
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <h4 class="mb-0">
            <i class="fas fa-gift"></i> {{ $t('pre_checkin_offers.title') }}
          </h4>
          <div class="stats-summary">
            <span class="badge badge-info mr-2">Total: {{ stats.total_bookings || 0 }}</span>
            <span class="badge badge-primary mr-2">With Offers: {{ stats.total_applied_offers || 0 }}</span>
            <span class="badge badge-secondary mr-2">No Offers: {{ stats.bookings_without_offers || 0 }}</span>
            <span class="badge badge-warning mr-2">Pending: {{ stats.pending_refunds || 0 }}</span>
            <span class="badge badge-success mr-2">Processed: {{ stats.processed_refunds || 0 }}</span>
            <span class="badge badge-danger">Cancelled: {{ stats.cancelled_offers || 0 }}</span>
          </div>
        </div>
      </template>

      <!-- Filters -->
      <div class="filters-section mb-4">
        <b-row>
          <b-col md="3">
            <b-form-group :label="$t('offers.hotel_id')">
              <b-form-select v-model="filters.hotel_id" :options="hotelOptions" @change="loadData">
                <option value="">{{ $t('offers.all_hotels') }}</option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group :label="$t('common.date') + ' From'">
              <b-form-input v-model="filters.date_from" type="date" @change="loadData"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group :label="$t('common.date') + ' To'">
              <b-form-input v-model="filters.date_to" type="date" @change="loadData"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group :label="$t('pre_checkin_offers.refund_status')">
              <b-form-select v-model="filters.refund_status" :options="refundStatusOptions" @change="loadData">
                <option value="">{{ $t('common.all_status') }}</option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Offer Status">
              <b-form-select v-model="filters.offer_status" :options="offerStatusOptions" @change="loadData">
                <option value="">All Bookings</option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="text-center p-4">
        <b-spinner class="mb-2" />
        <div>{{ $t('common.loading') }} {{ $t('pre_checkin_offers.title').toLowerCase() }}...</div>
      </div>

      <!-- Data Table -->
      <div v-else>
        <b-table :items="preCheckins" :fields="translatedTableFields" striped hover responsive class="mt-3">
          <template #cell(booking_info)="data">
            <div>
              <strong>Booking #{{ data.item.booking.id }}</strong><br>
              <small class="text-muted">{{ data.item.booking.hotel?.name }}</small><br>
              <small class="text-muted">{{ data.item.booking.user?.full_name }}</small>
            </div>
          </template>

          <template #cell(offer_info)="data">
            <div v-if="data.item.offer">
              <strong>{{ data.item.offer.name }}</strong><br>
              <small class="text-muted">{{ data.item.offer.type }} - {{ data.item.offer.value }}</small>
            </div>
            <div v-else class="text-muted">
              <i class="fas fa-times-circle text-danger"></i> No offer applied
            </div>
          </template>

          <template #cell(discount_info)="data">
            <div v-if="data.item.precheckin_discount_amount > 0">
              <strong>₹{{ data.item.precheckin_discount_amount }}</strong><br>
              <small class="text-muted">Final: ₹{{ data.item.final_price }}</small>
            </div>
            <div v-else class="text-muted">
              <i class="fas fa-minus"></i> No discount
            </div>
          </template>

          <template #cell(refund_status)="data">
            <div v-if="data.item.precheckin_discount_amount > 0">
              <span :class="`badge ${getRefundStatusBadgeClass(data.item.refund_status)}`">
                {{ getRefundStatusText(data.item.refund_status) }}
              </span>
            </div>
            <div v-else>
              <span class="badge badge-secondary">
                <i class="fas fa-minus"></i> No refund needed
              </span>
            </div>
          </template>

          <template #cell(refund_info)="data">
            <div v-if="data.item.precheckin_discount_amount > 0">
              <div v-if="data.item.refund_amount">
                <strong>₹{{ data.item.refund_amount }}</strong><br>
                <small class="text-muted">{{ data.item.refund_method }}</small><br>
                <small class="text-muted">{{ data.item.refund_date }}</small>
                <div v-if="data.item.refund_reference" class="mt-1">
                  <small class="text-info">Ref: {{ data.item.refund_reference }}</small>
                </div>
              </div>
              <div v-else-if="data.item.refund_status === 'pending'">
                <span class="badge badge-warning">Pending</span><br>
                <small class="text-muted" v-if="data.item.estimated_refund_amount">
                  Est: ₹{{ data.item.estimated_refund_amount }}
                </small>
              </div>
              <span v-else class="text-muted">{{ $t('pre_checkin_offers.no_refund') }}</span>
            </div>
            <div v-else class="text-muted">
              <i class="fas fa-minus"></i> Not applicable
            </div>
          </template>

          <template #cell(actions)="data">
            <div class="btn-group" role="group">
              <b-button size="sm" variant="info" @click="viewDetails(data.item)"
                :title="$t('pre_checkin_offers.view_details')">
                <i class="fas fa-eye"></i>
              </b-button>

              <b-button v-if="canProcessRefund(data.item)" size="sm" variant="success" @click="processRefund(data.item)"
                :title="$t('pre_checkin_offers.process_refund')">
                <i class="fas fa-money-bill-wave"></i>
              </b-button>

              <b-button v-if="canProcessRefund(data.item)" size="sm" variant="warning"
                @click="markRefundPending(data.item)" :title="$t('pre_checkin_offers.mark_pending')">
                <i class="fas fa-clock"></i>
              </b-button>

              <b-button v-if="canCancelOffer(data.item)" size="sm" variant="danger" @click="cancelOffer(data.item)"
                :title="$t('pre_checkin_offers.cancel_offer')">
                <i class="fas fa-times"></i>
              </b-button>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="d-flex justify-content-center mt-3">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"
            @change="loadData"></b-pagination>
        </div>
      </div>
    </b-card>

    <!-- Refund Modal -->
    <b-modal v-model="showRefundModal" :title="$t('pre_checkin_offers.process_refund')" @ok="handleRefundSubmit"
      @cancel="resetRefundForm">
      <b-form>
        <b-form-group :label="$t('pre_checkin_offers.refund_amount')">
          <b-form-input v-model="refundForm.refund_amount" type="number" step="0.01"
            :max="selectedItem?.precheckin_discount_amount" required></b-form-input>
        </b-form-group>

        <b-form-group :label="$t('pre_checkin_offers.refund_method')">
          <b-form-select v-model="refundForm.refund_method" :options="refundMethodOptions" required></b-form-select>
        </b-form-group>

        <b-form-group :label="$t('pre_checkin_offers.refund_date')">
          <b-form-input v-model="refundForm.refund_date" type="date" required></b-form-input>
        </b-form-group>

        <b-form-group :label="$t('pre_checkin_offers.refund_notes')">
          <b-form-textarea v-model="refundForm.refund_notes" rows="3"></b-form-textarea>
        </b-form-group>

        <b-form-group label="Refund Reference (Optional)">
          <b-form-input v-model="refundForm.refund_reference"
            placeholder="Transaction ID, Receipt No, etc."></b-form-input>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- Mark Refund Pending Modal -->
    <b-modal v-model="showPendingModal" :title="$t('pre_checkin_offers.mark_pending')" @ok="handlePendingSubmit"
      @cancel="resetPendingForm">
      <b-form>
        <b-form-group :label="$t('pre_checkin_offers.refund_notes')">
          <b-form-textarea v-model="pendingForm.refund_notes" rows="3"></b-form-textarea>
        </b-form-group>

        <b-form-group label="Estimated Refund Amount">
          <b-form-input v-model="pendingForm.estimated_refund_amount" type="number" step="0.01"
            placeholder="Estimated amount to be refunded"></b-form-input>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- Cancel Offer Modal -->
    <b-modal v-model="showCancelModal" :title="$t('pre_checkin_offers.cancel_offer')" @ok="handleCancelSubmit"
      @cancel="resetCancelForm">
      <b-form>
        <b-form-group :label="$t('pre_checkin_offers.cancellation_reason')">
          <b-form-textarea v-model="cancelForm.cancellation_reason" rows="3" required></b-form-textarea>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { request } from '@/Util/Request';

export default {
  name: 'PreCheckinOffersList',
  data() {
    return {
      isLoading: false,
      preCheckins: [],
      stats: {},
      currentPage: 1,
      perPage: 20,
      totalRows: 0,

      filters: {
        hotel_id: '',
        date_from: '',
        date_to: '',
        refund_status: '',
        offer_status: ''
      },

      showRefundModal: false,
      showCancelModal: false,
      showPendingModal: false,
      selectedItem: null,

      refundForm: {
        refund_amount: '',
        refund_method: '',
        refund_date: '',
        refund_notes: '',
        refund_reference: ''
      },

      cancelForm: {
        cancellation_reason: ''
      },

      pendingForm: {
        refund_notes: '',
        estimated_refund_amount: ''
      },

      tableFields: [
        { key: 'booking_info', label: 'Booking Info' },
        { key: 'offer_info', label: 'Offer' },
        { key: 'discount_info', label: 'Discount' },
        { key: 'refund_status', label: 'Status' },
        { key: 'refund_info', label: 'Refund Info' },
        { key: 'actions', label: 'Actions' }
      ],

      hotelOptions: [],
      refundStatusOptions: [
        { value: 'pending', text: 'Pending' },
        { value: 'processed', text: 'Processed' },
        { value: 'cancelled', text: 'Cancelled' }
      ],
      offerStatusOptions: [
        { value: 'with_offers', text: 'With Offers' },
        { value: 'without_offers', text: 'Without Offers' },
        { value: 'needs_refund', text: 'Needs Refund' }
      ],
      refundMethodOptions: [
        { value: 'bank_transfer', text: 'Bank Transfer' },
        { value: 'upi', text: 'UPI' },
        { value: 'cash', text: 'Cash' },
        { value: 'credit_note', text: 'Credit Note' }
      ]
    };
  },

  computed: {
    translatedTableFields() {
      return [
        { key: 'booking_info', label: this.$t('pre_checkin_offers.booking_info') },
        { key: 'offer_info', label: this.$t('offers.title') },
        { key: 'discount_info', label: this.$t('pre_checkin_offers.discount_info') },
        { key: 'refund_status', label: this.$t('pre_checkin_offers.refund_status') },
        { key: 'refund_info', label: this.$t('pre_checkin_offers.refund_info') },
        { key: 'actions', label: this.$t('common.actions') }
      ];
    }
  },

  async mounted() {
    await this.loadData();
    await this.loadStats();
  },

  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        const params = {
          page: this.currentPage,
          ...this.filters
        };

        const response = await request({
          method: 'get',
          url: '/admin/pre-checkin-offers',
          params
        });

        if (response.success) {
          this.preCheckins = response.data.data;
          this.totalRows = response.data.total;
          this.perPage = response.data.per_page;
        }
      } catch (error) {
        console.error('Failed to load pre-checkin offers:', error);
        this.$bvToast.toast(this.$t('common.error_occurred'), {
          title: this.$t('common.error'),
          variant: 'danger'
        });
      } finally {
        this.isLoading = false;
      }
    },

    async loadStats() {
      try {
        const response = await request({
          method: 'get',
          url: '/admin/pre-checkin-offers-stats'
        });

        if (response.success) {
          this.stats = response.data;
        }
      } catch (error) {
        console.error('Failed to load stats:', error);
      }
    },

    viewDetails(item) {
      // Navigate to details page
      this.$router.push(`/admin/pre-checkin-offers/${item.id}`);
    },

    processRefund(item) {
      this.selectedItem = item;
      this.refundForm.refund_amount = item.precheckin_discount_amount;
      this.refundForm.refund_date = new Date().toISOString().split('T')[0];
      this.showRefundModal = true;
    },

    markRefundPending(item) {
      this.selectedItem = item;
      this.pendingForm.refund_notes = '';
      this.showPendingModal = true;
    },

    cancelOffer(item) {
      this.selectedItem = item;
      this.showCancelModal = true;
    },

    async handleRefundSubmit() {
      try {
        const response = await request({
          method: 'post',
          url: `/admin/pre-checkin-offers/${this.selectedItem.id}/process-refund`,
          data: this.refundForm
        });

        if (response.success) {
          this.$bvToast.toast(this.$t('pre_checkin_offers.refund_processed_success'), {
            title: this.$t('common.success'),
            variant: 'success'
          });
          this.resetRefundForm();
          await this.loadData();
          await this.loadStats();
        }
      } catch (error) {
        this.$bvToast.toast(this.$t('pre_checkin_offers.failed_to_process_refund'), {
          title: this.$t('common.error'),
          variant: 'danger'
        });
      }
    },

    async handlePendingSubmit() {
      try {
        const response = await request({
          method: 'post',
          url: `/admin/pre-checkin-offers/${this.selectedItem.id}/mark-refund-pending`,
          data: this.pendingForm
        });

        if (response.success) {
          this.$bvToast.toast(this.$t('pre_checkin_offers.refund_marked_pending'), {
            title: this.$t('common.success'),
            variant: 'success'
          });
          this.resetPendingForm();
          await this.loadData();
          await this.loadStats();
        }
      } catch (error) {
        this.$bvToast.toast(this.$t('pre_checkin_offers.failed_to_process_refund'), {
          title: this.$t('common.error'),
          variant: 'danger'
        });
      }
    },

    async handleCancelSubmit() {
      try {
        const response = await request({
          method: 'post',
          url: `/admin/pre-checkin-offers/${this.selectedItem.id}/cancel-offer`,
          data: this.cancelForm
        });

        if (response.success) {
          this.$bvToast.toast(this.$t('pre_checkin_offers.offer_cancelled_success'), {
            title: this.$t('common.success'),
            variant: 'success'
          });
          this.resetCancelForm();
          await this.loadData();
          await this.loadStats();
        }
      } catch (error) {
        this.$bvToast.toast(this.$t('pre_checkin_offers.failed_to_cancel_offer'), {
          title: this.$t('common.error'),
          variant: 'danger'
        });
      }
    },

    resetRefundForm() {
      this.refundForm = {
        refund_amount: '',
        refund_method: '',
        refund_date: '',
        refund_notes: '',
        refund_reference: ''
      };
      this.selectedItem = null;
      this.showRefundModal = false;
    },

    resetCancelForm() {
      this.cancelForm = {
        cancellation_reason: ''
      };
      this.selectedItem = null;
      this.showCancelModal = false;
    },

    resetPendingForm() {
      this.pendingForm = {
        refund_notes: '',
        estimated_refund_amount: ''
      };
      this.selectedItem = null;
      this.showPendingModal = false;
    },

    getRefundStatusBadgeClass(status) {
      switch (status) {
        case 'pending':
          return 'badge-warning';
        case 'processed':
          return 'badge-success';
        case 'cancelled':
          return 'badge-danger';
        default:
          return 'badge-secondary';
      }
    },

    canProcessRefund(item) {
      return item.refund_status !== 'processed' && item.offer_id && item.precheckin_discount_amount > 0;
    },

    canCancelOffer(item) {
      return item.offer_id && item.precheckin_discount_amount > 0;
    },

    getRefundStatusText(status) {
      switch (status) {
        case 'pending':
          return 'Pending';
        case 'processed':
          return 'Processed';
        case 'cancelled':
          return 'Cancelled';
        default:
          return 'No refund';
      }
    }
  }
};
</script>

<style scoped>
.pre-checkin-offers-management {
  padding: 20px;
}

.stats-summary {
  display: flex;
  gap: 10px;
}

.filters-section {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.btn-group .btn {
  margin-right: 5px;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}
</style>